import React, { useState } from "react";
import {
  MdAttachMoney,
  MdCheck,
  MdCheckCircle,
  MdFavorite,
  MdGroups,
  MdHome,
  MdPeople,
  MdVolunteerActivism,
  MdWbSunny,
} from "react-icons/md";
import Badge from "components/badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import Chart from "components/charts/Chart";
import DataTable from "components/data-table";
import { Select } from "components/fields";
import { PageContainer, PageHeader } from "components/page";
import StatCard, { TrendIndicator } from "components/stat-card";
import { palette } from "lib/chartTheme";
import { formatCurrency, formatDate, formatNumber } from "utils/format";

const CAMPUSES = [
  { value: "all", label: "All Campuses" },
  { value: "1", label: "Main Campus - Dallas" },
  { value: "2", label: "North Campus - Plano" },
  { value: "3", label: "South Campus - Richardson" },
  { value: "4", label: "Online Campus" },
];

const DATE_RANGES = [
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
];

const sparklineOptions = (formatter) => ({
  chart: { sparkline: { enabled: true } },
  colors: [palette.sage],
  plotOptions: { bar: { columnWidth: "70%", borderRadius: 2 } },
  tooltip: { x: { show: false }, y: { formatter } },
});

const formatWholeCurrency = (value) => formatCurrency(value, { maximumFractionDigits: 0 });

const HERO_KPIS = [
  {
    label: "Weekly Attendance",
    value: "2,320",
    caption: "4-week avg: 2,156 | +7.6% vs last month",
    trend: 7.6,
    icon: MdPeople,
    sparkline: [1890, 1950, 2020, 2100, 2156, 2210, 2280, 2320],
    sparklineOptions: sparklineOptions(formatNumber),
  },
  {
    label: "First-Time Visitors",
    value: "34",
    caption: "Monthly avg: 26 | YTD: 412",
    trend: 30.8,
    icon: MdFavorite,
    sparkline: [22, 24, 28, 23, 31, 26, 29, 34],
    sparklineOptions: sparklineOptions(formatNumber),
  },
  {
    label: "Salvations (YTD)",
    value: "187",
    caption: "This month: 18 | Avg: 16/month",
    trend: 12.5,
    icon: MdCheckCircle,
    sparkline: [12, 14, 13, 16, 15, 17, 16, 18],
    sparklineOptions: sparklineOptions(formatNumber),
  },
  {
    label: "Weekly Giving",
    value: "$48,250",
    caption: "Budget: $45,000 | +7.2% vs goal",
    trend: 7.2,
    icon: MdAttachMoney,
    sparkline: [42000, 43500, 44200, 45800, 46200, 47100, 47800, 48250],
    sparklineOptions: sparklineOptions(formatWholeCurrency),
  },
];

const ATTENDANCE_BREAKDOWN = [
  { category: "In-Person", count: 1687, share: 73, change: 5.8 },
  { category: "Kids Ministry", count: 365, share: 16, change: 11.2 },
  { category: "Online", count: 234, share: 10, change: 18.3 },
  { category: "Volunteers", count: 34, share: 1, change: 3.0 },
  { category: "Total", count: 2320, share: 100, change: 7.6, isTotal: true },
];

const getAttendanceRowId = (row) => row.category;

const ATTENDANCE_COLUMNS = [
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => (
      <span className={`text-fg ${row.original.isTotal ? "font-bold" : "font-medium"}`}>
        {row.original.category}
      </span>
    ),
  },
  {
    accessorKey: "count",
    header: "Count",
    meta: { align: "center" },
    cell: ({ row }) => (
      <span className={`font-bold text-fg ${row.original.isTotal ? "text-xl" : "text-lg"}`}>
        {formatNumber(row.original.count)}
      </span>
    ),
  },
  {
    accessorKey: "share",
    header: "% of Total",
    meta: { align: "center" },
    cell: ({ getValue }) => <span className="text-sm font-semibold text-fg">{getValue()}%</span>,
  },
  {
    accessorKey: "change",
    header: "vs Last Week",
    meta: { align: "center" },
    cell: ({ getValue }) => <TrendIndicator value={getValue()} />,
  },
];

const HIGH_CAPACITY = 85;

const capacityFill = (capacity) => {
  if (capacity >= HIGH_CAPACITY) return "bg-danger";
  if (capacity >= 70) return "bg-warning";
  return "bg-success";
};

const SERVICES = [
  { name: "Sunday Morning", time: "8:30 AM", attendance: 512, capacity: 91, trend: 6.2 },
  { name: "Sunday Morning", time: "10:00 AM", attendance: 687, capacity: 95, trend: 4.8 },
  { name: "Sunday Morning", time: "11:45 AM", attendance: 598, capacity: 87, trend: 3.5 },
  { name: "Saturday Evening", time: "5:00 PM", attendance: 289, capacity: 64, trend: 8.1 },
  { name: "Online Service", time: "Various", attendance: 234, capacity: 52, trend: 18.3 },
];

const SERVICE_COLUMNS = [
  {
    accessorKey: "name",
    header: "Service",
    cell: ({ row }) => (
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-fg">{row.original.name}</p>
          {row.original.capacity >= HIGH_CAPACITY && (
            <Badge tone="success" size="sm">
              High Demand
            </Badge>
          )}
        </div>
        <p className="text-sm text-fg-muted">{row.original.time}</p>
      </div>
    ),
  },
  {
    accessorKey: "attendance",
    header: "Attendance",
    meta: { align: "center" },
    cell: ({ getValue }) => (
      <span className="text-lg font-bold text-fg">{formatNumber(getValue())}</span>
    ),
  },
  {
    accessorKey: "capacity",
    header: "Capacity",
    meta: { align: "center" },
    cell: ({ getValue }) => {
      const capacity = getValue();
      return (
        <div className="flex items-center justify-center gap-2">
          <div className="h-2 w-20 overflow-hidden rounded-full bg-surface-subtle">
            <div className={`h-full ${capacityFill(capacity)}`} style={{ width: `${capacity}%` }} />
          </div>
          <span className="text-sm font-semibold text-fg">{capacity}%</span>
        </div>
      );
    },
  },
  {
    accessorKey: "trend",
    header: "Trend",
    meta: { align: "center" },
    cell: ({ getValue }) => <TrendIndicator value={getValue()} />,
  },
];

const getServiceRowId = (row) => `${row.name}-${row.time}`;

const HEARTBEAT_STATUS = {
  urgent: { label: "Urgent", tone: "danger", fill: "bg-danger" },
  followup: { label: "Follow-up", tone: "warning", fill: "bg-warning" },
  good: { label: "Good", tone: "success", fill: "bg-success" },
};

const HEARTBEAT_RESPONSES = [
  { name: "Jennifer Martinez", message: "Loved the worship service today! The sermon really spoke to me about family priorities...", status: "good", time: "1 hour ago" },
  { name: "David Thompson", message: "Going through a tough season with work stress and need prayer support", status: "followup", time: "3 hours ago" },
  { name: "Emily Rodriguez", message: "Haven't been able to connect with my small group leader. Looking for guidance...", status: "followup", time: "5 hours ago" },
  { name: "Michael Chen", message: "Incredible experience at the new member class! Excited to serve in kids ministry", status: "good", time: "8 hours ago" },
  { name: "Sarah Williams", message: "Family emergency - father hospitalized. Could use urgent prayer and support", status: "urgent", time: "1 day ago" },
];

const GROWTH_INSIGHTS = [
  { label: "Most Represented Age", value: "35-44", caption: "32% of congregation", detail: "25-34: 28% • 45-54: 22%", icon: MdGroups },
  { label: "New Movers (12mo)", value: "18%", caption: "Excellent prospects!", captionTone: "text-success-fg", detail: "256 families • +45% YoY", icon: MdHome },
  { label: "Volunteer Participation", value: "42%", caption: "Active volunteers", detail: "587 people • Target: 50%", icon: MdVolunteerActivism },
  { label: "Median HH Income", value: "$95k", caption: "Household median", detail: "Area avg: $87k", icon: MdAttachMoney },
];

const UPCOMING_SERVICES = [
  { name: "Saturday Evening", when: "Tomorrow, 5:00 PM", status: { label: "All Set", tone: "success" }, detail: "Volunteers: 16/16 assigned • Rooms ready" },
  { name: "Sunday Morning (8:30 AM)", when: "Sunday, 8:30 AM", status: { label: "1 Spot", tone: "warning" }, detail: "Volunteers: 23/24 assigned • Tech team ready" },
  { name: "Sunday Morning (10:00 AM)", when: "Sunday, 10:00 AM", status: { label: "All Set", tone: "success" }, detail: "Volunteers: 28/28 assigned • Kids ministry staffed" },
  { name: "Sunday Morning (11:45 AM)", when: "Sunday, 11:45 AM", status: { label: "All Set", tone: "success" }, detail: "Volunteers: 26/26 assigned • Worship team confirmed" },
];

const ACTION_TONES = {
  danger: { panel: "border-danger/20 bg-danger-subtle", marker: "bg-danger" },
  warning: { panel: "border-warning/20 bg-warning-subtle", marker: "bg-warning" },
  info: { panel: "border-info/20 bg-info-subtle", marker: "bg-info" },
  success: { panel: "border-success/20 bg-success-subtle", marker: "bg-success" },
  neutral: { panel: "border-line bg-surface-subtle", marker: "bg-fg-muted" },
};

const ACTION_ITEMS = [
  { tone: "danger", marker: "5", title: "Urgent Care Needs", description: "From Heartbeat responses - immediate attention required", action: "Review" },
  { tone: "warning", marker: "18", title: "Follow-up Contacts", description: "Members who need connection within 48 hours", action: "Assign" },
  { tone: "info", marker: <MdCheck aria-hidden="true" className="h-4 w-4" />, title: "Planning Center Synced", description: "Last synced 1 hour ago • Auto-sync enabled", action: "View" },
  { tone: "success", marker: <MdCheck aria-hidden="true" className="h-4 w-4" />, title: "Church Metrics Synced", description: "Successfully synced 2,340 records • Auto-sync: Daily 2AM", action: "Details" },
  { tone: "neutral", marker: "34", title: "First-Time Visitor Follow-ups", description: "From this week's services - send welcome messages", action: "Start" },
];

/** Static forecast for the demo's upcoming Sunday. */
const WeatherWidget = () => (
  <div className="flex items-center gap-3 pr-2">
    <MdWbSunny aria-hidden="true" className="h-10 w-10 shrink-0 text-warning-fg" />
    <div>
      <p className="text-xs font-medium text-fg-muted">This Sunday</p>
      <p className="text-xl font-bold leading-tight text-fg">74°F</p>
      <p className="text-xs text-fg-muted">Sunny - Perfect weather for service!</p>
    </div>
    <div className="text-right">
      <p className="text-xs text-fg-muted">Dallas, TX</p>
      <p className="mt-1 text-xs text-fg-muted">{formatDate("2025-10-20")}</p>
    </div>
  </div>
);

const Section = ({ id, title, children }) => (
  <section aria-labelledby={id} className="flex flex-col gap-3">
    <h2 id={id} className="text-heading">
      {title}
    </h2>
    {children}
  </section>
);

const HeartbeatResponseItem = ({ name, message, status, time }) => {
  const config = HEARTBEAT_STATUS[status];
  return (
    <li className="flex items-start gap-3 py-3">
      <div
        aria-hidden="true"
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${config.fill}`}
      >
        <div className="h-3 w-3 rounded-full bg-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <p className="text-sm font-semibold text-fg">{name}</p>
          <Badge tone={config.tone} size="sm">
            {config.label}
          </Badge>
        </div>
        <p className="line-clamp-1 text-sm text-fg-muted">{message}</p>
        <p className="mt-1 text-xs text-fg-muted">{time}</p>
      </div>
    </li>
  );
};

const EngagementMetric = ({ label, value, valueTone = "text-fg", caption, panel = "bg-surface-raised" }) => (
  <div className={`rounded-control p-4 ${panel}`}>
    <p className="mb-1 text-sm text-fg-muted">{label}</p>
    <p className={`text-2xl font-bold ${valueTone}`}>{value}</p>
    <div className="mt-1 text-xs text-fg-muted">{caption}</div>
  </div>
);

const InvestorDemo = () => {
  const [selectedCampus, setSelectedCampus] = useState("all");
  const [dateRange, setDateRange] = useState("week");

  return (
    <PageContainer>
      <PageHeader
        title="Investor Demo"
        description="Church Health Dashboard: real-time insights across all campuses • Updated just now"
        actions={
          <>
            <WeatherWidget />
            <Select
              aria-label="Campus"
              className="w-60"
              value={selectedCampus}
              onChange={(e) => setSelectedCampus(e.target.value)}
              options={CAMPUSES}
            />
            <Select
              aria-label="Time Range"
              className="w-40"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              options={DATE_RANGES}
            />
          </>
        }
      />

      <Section id="investor-demo-overview" title="Church Health Overview">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {HERO_KPIS.map((kpi) => (
            <StatCard
              key={kpi.label}
              size="lg"
              icon={kpi.icon}
              accent="sage"
              label={kpi.label}
              value={kpi.value}
              trend={{ value: kpi.trend }}
              caption={kpi.caption}
            />
          ))}
        </div>
        <Card padding="md">
          <CardHeader titleAs="h3" title="Eight-Week Trends" description="Weekly values behind each metric above" />
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {HERO_KPIS.map((kpi) => (
              <div key={kpi.label}>
                <p className="mb-2 text-sm font-medium text-fg-muted">{kpi.label}</p>
                <Chart
                  type="bar"
                  height={56}
                  series={[{ name: kpi.label, data: kpi.sparkline }]}
                  options={kpi.sparklineOptions}
                />
              </div>
            ))}
          </div>
        </Card>
      </Section>

      <Section id="investor-demo-attendance" title="Attendance Insights">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-3">
            <h3 className="text-heading">Attendance Breakdown</h3>
            <DataTable
              aria-label="Attendance breakdown"
              columns={ATTENDANCE_COLUMNS}
              data={ATTENDANCE_BREAKDOWN}
              getRowId={getAttendanceRowId}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-3">
            <h3 className="text-heading">Service Performance</h3>
            <DataTable
              aria-label="Service performance"
              columns={SERVICE_COLUMNS}
              data={SERVICES}
              getRowId={getServiceRowId}
            />
          </div>
        </div>
      </Section>

      <Section id="investor-demo-care" title="Connection & Care">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card padding="md">
            <CardHeader titleAs="h3" title="Heartbeat Engagement" />
            <div className="mb-6 mt-4 grid grid-cols-2 gap-4">
              <EngagementMetric
                label="Active Connections"
                value="1,247"
                caption={
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <TrendIndicator value={12.3} />
                    this month
                  </span>
                }
              />
              <EngagementMetric
                label="Response Rate"
                value="86%"
                valueTone="text-success-fg"
                caption="Industry avg: 62%"
              />
              <EngagementMetric
                label="Urgent Care Needs"
                value="5"
                valueTone="text-danger-fg"
                caption="Requires immediate attention"
                panel="bg-danger-subtle"
              />
              <EngagementMetric
                label="Follow-up Needed"
                value="18"
                valueTone="text-warning-fg"
                caption="Within 48 hours"
                panel="bg-warning-subtle"
              />
            </div>
            <Button variant="primary" size="lg" fullWidth>
              View All Connections
            </Button>
          </Card>

          <Card padding="md">
            <CardHeader
              titleAs="h3"
              title="Recent Heartbeat Responses"
              actions={
                <Button variant="secondary" size="sm">
                  View All
                </Button>
              }
            />
            <ul className="mt-2 divide-y divide-line">
              {HEARTBEAT_RESPONSES.map((response) => (
                <HeartbeatResponseItem key={response.name} {...response} />
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      <Section id="investor-demo-growth" title="Growth Insights">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {GROWTH_INSIGHTS.map((insight) => (
            <StatCard
              key={insight.label}
              size="lg"
              icon={insight.icon}
              accent="sage"
              label={insight.label}
              value={insight.value}
              caption={
                <>
                  <span className={`block text-sm ${insight.captionTone || "text-fg-muted"}`}>
                    {insight.caption}
                  </span>
                  <span className="mt-3 block border-t border-line pt-3">{insight.detail}</span>
                </>
              }
            />
          ))}
        </div>
      </Section>

      <Section id="investor-demo-week" title="This Week & Action Items">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card padding="md">
            <CardHeader titleAs="h3" title="Upcoming Services" />
            <ul className="mt-4 flex flex-col gap-3">
              {UPCOMING_SERVICES.map((service) => (
                <li key={service.name} className="rounded-control bg-surface-raised px-4 py-3">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="font-semibold text-fg">{service.name}</p>
                    <Badge tone={service.status.tone}>{service.status.label}</Badge>
                  </div>
                  <p className="text-sm text-fg-muted">{service.when}</p>
                  <p className="mt-1 text-xs text-fg-muted">{service.detail}</p>
                </li>
              ))}
            </ul>
          </Card>

          <Card padding="md">
            <CardHeader titleAs="h3" title="Priority Action Items" />
            <ul className="mt-4 flex flex-col gap-3">
              {ACTION_ITEMS.map((item) => {
                const tone = ACTION_TONES[item.tone];
                return (
                  <li
                    key={item.title}
                    className={`flex items-start gap-3 rounded-control border p-3 ${tone.panel}`}
                  >
                    <div
                      className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${tone.marker}`}
                    >
                      {item.marker}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-fg">{item.title}</p>
                      <p className="mt-1 text-xs text-fg-muted">{item.description}</p>
                    </div>
                    <Button variant="secondary" size="sm">
                      {item.action}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      </Section>
    </PageContainer>
  );
};

export default InvestorDemo;
